import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { History, Download } from 'lucide-react';
import { format } from 'date-fns';
import './pages.css';
import '../components/AdvancedDashboard.css';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { useAuth } from '../auth';
import { LoadingState, EmptyState, ErrorState } from '../components/ui/States';
import { useToast } from '../components/ui/Toast';
import { fetchPanelFieldHistory, fetchWeatherFieldHistory } from '../historyApi';
import { buildCsv, sanitizeFilename, downloadCsv } from '../csvExport';

// unit is used both for the axis/label text and, unchanged, as the CSV
// "unit" column - a single source of truth instead of a second mapping
// table that could drift out of sync.
const PANEL_FIELD_OPTIONS = [
  { value: 'power_w', label: 'Puissance (W)', unit: 'W' },
  { value: 'voltage_v', label: 'Tension (V)', unit: 'V' },
  { value: 'current_a', label: 'Courant (A)', unit: 'A' },
  { value: 'temp_panel_c', label: 'Température Panneau (°C)', unit: '°C' }
];
const WEATHER_FIELD_OPTIONS = [
  { value: 'temp_air_c', label: 'Température Air (°C)', unit: '°C' },
  { value: 'humidity_pct', label: 'Humidité (%)', unit: '%' },
  { value: 'light_lvl', label: 'Lumière (%)', unit: '%' },
  { value: 'light_lux', label: 'Lumière (lux)', unit: 'lux' },
  { value: 'wind_ms', label: 'Vent (m/s)', unit: 'm/s' }
];
const RANGES = ['1h', '6h', '24h', '7d'];

export default function HistoryPage() {
  const { plants, realTimeData, weatherData } = useOutletContext();
  const { token } = useAuth();
  const toast = useToast();
  const safePlants = Array.isArray(plants) ? plants : [];

  const [plantId, setPlantId] = useState('');
  const [entityType, setEntityType] = useState('panel');
  const [entityId, setEntityId] = useState('');
  const [field, setField] = useState('power_w');
  const [range, setRange] = useState('1h');
  const [data, setData] = useState([]);
  const [status, setStatus] = useState('empty');
  // Raw, untransformed API rows for the current selection - kept separately
  // from `data` (which is reshaped for the chart: reformatted time label,
  // null points dropped so the line doesn't render fake connections) so CSV
  // export can use the real values/timestamps/nulls exactly as the backend
  // returned them, without a second request.
  const [rawRows, setRawRows] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!plantId && safePlants.length > 0) setPlantId(safePlants[0].plant_id);
  }, [safePlants, plantId]);

  const panelIds = useMemo(() => {
    const arr = Array.from(realTimeData?.values?.() || []).filter(d => d.plant_id === plantId);
    return [...new Set(arr.map(d => d.panel_id))];
  }, [realTimeData, plantId]);

  const stationIds = useMemo(() => {
    const arr = Array.from(weatherData?.values?.() || []).filter(w => w.plant_id === plantId);
    return [...new Set(arr.map(w => w.station_id).filter(Boolean))];
  }, [weatherData, plantId]);

  useEffect(() => {
    const ids = entityType === 'panel' ? panelIds : stationIds;
    if (!ids.includes(entityId)) setEntityId(ids[0] || '');
  }, [entityType, panelIds, stationIds, entityId]);

  useEffect(() => {
    setField(entityType === 'panel' ? 'power_w' : 'temp_air_c');
  }, [entityType]);

  useEffect(() => {
    if (!plantId || !entityId) {
      setData([]);
      setRawRows([]);
      setStatus('empty');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    const req = entityType === 'panel'
      ? fetchPanelFieldHistory(token, plantId, entityId, field, range)
      : fetchWeatherFieldHistory(token, plantId, entityId, field, range);

    req.then(rows => {
      if (cancelled) return;
      setRawRows(rows);
      const points = rows
        .filter(r => r._value !== null && r._value !== undefined)
        .map(r => ({ time: format(new Date(r._time), 'HH:mm:ss'), value: r._value }));
      setData(points);
      setStatus(points.length > 0 ? 'ok' : 'empty');
    }).catch(() => {
      if (cancelled) return;
      setData([]);
      setRawRows([]);
      setStatus('error');
    });

    return () => { cancelled = true; };
  }, [token, plantId, entityType, entityId, field, range]);

  const fieldOptions = entityType === 'panel' ? PANEL_FIELD_OPTIONS : WEATHER_FIELD_OPTIONS;
  const entityIds = entityType === 'panel' ? panelIds : stationIds;
  const selectedFieldOption = fieldOptions.find(f => f.value === field);
  const fieldLabel = selectedFieldOption?.label || field;

  const handleExportCsv = () => {
    if (exporting) return; // avoid duplicate-click double downloads
    if (rawRows.length === 0) {
      toast.info('Aucune donnée à exporter pour cette période');
      return;
    }
    setExporting(true);
    try {
      const unit = selectedFieldOption?.unit || '';
      const headers = ['timestamp', 'plant_id', 'device_id', 'metric', 'value', 'unit'];
      // Real API rows only - a null/missing bucket becomes an empty CSV
      // value (via buildCsv's escaping), never a fabricated 0, and no row
      // is invented or dropped relative to what the API actually returned.
      const csvRows = rawRows.map(r => [
        r._time,
        plantId,
        entityId,
        field,
        r._value === null || r._value === undefined ? '' : r._value,
        unit
      ]);
      const csvText = buildCsv(headers, csvRows);
      const dateStr = new Date().toISOString().slice(0, 10);
      const filename = sanitizeFilename(`${plantId}_${entityId}_${field}_${range}_${dateStr}.csv`);
      downloadCsv(filename, csvText);
      toast.success(`Export CSV généré (${rawRows.length} point${rawRows.length > 1 ? 's' : ''})`);
    } catch (err) {
      toast.error("Erreur lors de l'export CSV");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Tableau de bord', to: '/' }, { label: 'Historique' }]} />
      <div className="page-header">
        <h1><History size={22} /> Historique</h1>
      </div>

      <div className="summary-card" style={{ marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
        <div className="form-group" style={{ marginBottom: 0, minWidth: 160 }}>
          <label>Centrale</label>
          <select value={plantId} onChange={e => setPlantId(e.target.value)}>
            {safePlants.map(p => <option key={p.plant_id} value={p.plant_id}>{p.name || p.plant_id}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0, minWidth: 140 }}>
          <label>Type</label>
          <select value={entityType} onChange={e => setEntityType(e.target.value)}>
            <option value="panel">Panneau</option>
            <option value="weather">Station météo</option>
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0, minWidth: 140 }}>
          <label>{entityType === 'panel' ? 'Panneau' : 'Station'}</label>
          <select value={entityId} onChange={e => setEntityId(e.target.value)}>
            {entityIds.length === 0 && <option value="">Aucun</option>}
            {entityIds.map(id => <option key={id} value={id}>{id}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0, minWidth: 180 }}>
          <label>Mesure</label>
          <select value={field} onChange={e => setField(e.target.value)}>
            {fieldOptions.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Période</label>
          <div className="time-range-selector">
            {RANGES.map(r => (
              <button key={r} className={range === r ? 'active' : ''} onClick={() => setRange(r)}>{r.toUpperCase()}</button>
            ))}
          </div>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <button
            className="btn-secondary"
            onClick={handleExportCsv}
            disabled={exporting || rawRows.length === 0}
            title={rawRows.length === 0 ? 'Aucune donnée à exporter' : 'Exporter les données affichées en CSV'}
          >
            <Download size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {exporting ? 'Export en cours...' : 'Exporter CSV'}
          </button>
        </div>
      </div>

      <div className="chart-panel chart-main">
        <div className="chart-header"><h3>{fieldLabel} (historique réel)</h3></div>
        {status === 'loading' && <LoadingState label="Chargement..." />}
        {status === 'error' && <ErrorState label="Erreur lors du chargement de l'historique" />}
        {status === 'empty' && <EmptyState label="Aucune donnée historique pour cette période" />}
        {status === 'ok' && (
          <ResponsiveContainer width="100%" height={380}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#444" />
              <XAxis dataKey="time" stroke="#888" tick={{ fill: '#888' }} />
              <YAxis stroke="#888" tick={{ fill: '#888' }} />
              <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', borderRadius: '8px' }} />
              <Legend />
              <Line type="monotone" dataKey="value" stroke="#8884d8" name={fieldLabel} connectNulls={false} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
